import * as React from 'react';
import { findOne, removeDuplicates } from 'rcd-cpp-workspace/utils/array';
import { NodeType } from '../enums/NodeType';
import { Op } from '../enums/Op';
import {
  FieldSchemaInfo,
  Metadata,
  MetadataProps,
  Schema,
  SchemaField,
  Workspace,
  WorkspaceSetting
} from '../types/Metadata';
import { FieldType } from '../enums/FieldType';
import { View } from '../types/View';
import { useDrag, useDrop } from 'react-dnd';
import type { Identifier, XYCoord } from 'dnd-core';
import { API } from '../api';
import { FieldRequiredValue } from '../enums/FieldRequiredValue';
import { DataType } from '../enums/DataType';
import { ItemStatus, CreativeStatus } from '../enums/ItemStatus';
import { ApprovalTask } from '../enums/ApprovalTask';
import { FieldKey } from '../enums/FieldKey';
import { RelationshipFieldType } from '../enums/RelationshipFieldType';
import { ContentType } from '../enums/ContentType';
import { deepEquals } from 'rcd-cpp-workspace/utils/deepEquals';
import { ItemType } from '../enums/ItemType';
import { FieldShowValue } from '../enums/FieldShowValue';
import { useSelector } from './useSelector';
import { UserSetting, UserSettings } from '../types/UserSetting';
import { Role } from '../enums/Role';
import { ApprovalType } from '../enums/ApprovalType';
import { ContentTypeConfig } from '../types/ContentTypeConfiguration';
import { WorkflowPrioritization } from '../enums/WorkflowPrioritization';
import { ConfigureWorkflowType } from '../enums/ConfigureWorkflowType';
import { TreeFieldUtils } from './treeFieldUtils';
import { WorkflowInfo } from '../types/WorkflowInfo';
import { ConfigureWorkflowWarningType } from '../enums/ConfigureWorkflowWarningType';
import { ShowFieldsOption } from '../enums/ShowFieldsOption';
import { AppRefId } from './constants';
interface RawDate {
  day: number;
  hour: number;
  minute: number;
  month: number;
  nanosecond: number;
  second: number;
  timeZoneOffsetSeconds: number;
  year: number;
}

const marketDefinitionFields = [
  'status',
  'title',
  'summary',
  'lifecyclePhase',
  'buyingCenter',
  'function',
  'role',
  'primaryQuestion',
  'author'
];

const pilotFields = ['pilotType'];

const defaultFields = [
  'status',
  'title',
  'summary',
  'clientInterest',
  'unique',
  'function',
  'role',
  'author'
];
const magicQuadrantFields = [
  'market',
  'plannedReleaseDate',
  'reasonForMagicQuadrantRequest'
];
const globalSubmissionFields = [
  'status',
  'title',
  'summary',
  'confClientInterest',
  'confSessionType',
  'function',
  'role',
  'author',
  'conferenceSeries'
];
export const audienceDetailsFields = [
  'primaryQuestion',
  'additionalQuestions',
  'function',
  'role',
  'topicCategory',
  'taxonomyTag',
  'rolePriority',
  'priorityJourneyStep',
  'additionalTopicCategory',
  'additionalTaxonomyTag',
  'additionalFunctions',
  'additionalRoles',
  'primaryRolePriority',
  'primaryPriorityJourneyStep'
];

const getRequiredFields = (
  itemType: ItemType,
  itemStatusId: number,
  contentTypeId: number
) => {
  const isConceptReviewOrPush =
    itemStatusId == ItemStatus.Concept_Review ||
    itemStatusId == ItemStatus.Review_Done_Start_Production;

  if (itemType == ItemType.globalSubmission && isConceptReviewOrPush) {
    return globalSubmissionFields;
  } else if (itemType == ItemType.content && isConceptReviewOrPush) {
    if (contentTypeId == ContentType.MARKET_DEFINITION) {
      return marketDefinitionFields;
    }
    return defaultFields;
  }
  return [];
};

export const isRawDate = (d: any): d is RawDate =>
  d?.hasOwnProperty('year') &&
  d?.hasOwnProperty('month') &&
  d?.hasOwnProperty('day');

export const isRawDateTime = (d: any): RawDate =>
  d?.hasOwnProperty('year') &&
  d?.hasOwnProperty('month') &&
  d?.hasOwnProperty('day') &&
  d?.hasOwnProperty('hour') &&
  d?.hasOwnProperty('minute') &&
  d?.hasOwnProperty('second');

const parseRawDate = (d: RawDate) => {
  if (isRawDateTime(d)) {
    return new Date(
      Date.UTC(d.year, d.month - 1, d.day, d.hour, d.minute, d.second, 0)
    );
  } else if (isRawDate(d)) {
    return new Date(d.year, d.month - 1, d.day);
  } else {
    console.error('Unable to parse date without raw date fields');
  }
};

export const parseDate = (date: string | Date | number): Date => {
  if (!date) return null;
  if (typeof date == 'object' && isRawDate(date)) {
    const d = parseRawDate(date);
    if (d) return d;
  }
  if (typeof date == 'number') return new Date(date);
  if (typeof date == 'string') {
    const d = new Date(date);
    const t = d.getTime() + d.getTimezoneOffset() * 60000;
    return new Date(t);
  }
  return date;
};

export const formatDate = (date: string | Date, forSave?: boolean): string => {
  if (!date) return null;
  date = parseDate(date);
  if (forSave) {
    return [
      date.getFullYear(),
      ('0' + (date.getMonth() + 1)).slice(-2),
      ('0' + date.getDate()).slice(-2)
    ].join('-');
  }
  return Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date);
};

export const getMetadata = <M extends Metadata = Metadata>(
  metadata: Metadata[],
  label: NodeType
): M[] => metadata?.filter(m => m?.labels?.includes(label)) as M[];

export const getMetadataByRefId = <M extends Metadata = Metadata>(
  metadata: Metadata[],
  refId: string
): M => metadata?.filter(m => m?.props?.refId == refId)[0] as M;

export const isSystemSchema = (s: Schema) => {
  return s?.isSystemSchema || s?.baseSchema?.isSystemSchema;
};

export const getSchemaForm = (
  schema: Schema,
  data: Partial<Metadata> | string,
  includeAlwaysHideFields?: boolean
) => {
  if (schema?.props?.type == NodeType.Item && schema.schemaForms?.length > 0) {
    const itemType = typeof data == 'string' ? data : getItemType(data);
    for (const sf of schema.schemaForms) {
      if (sf?.props?.itemType == itemType) {
        if (includeAlwaysHideFields) {
          const allFields = [...sf.props.fields];
          schema.props.fields.forEach(f => {
            if (!f.parentField && !findOne(allFields, { field: f.field })) {
              allFields.push({ ...f, showAt: FieldShowValue.AlwaysHide });
            }
          });
          return {
            ...sf,
            props: {
              ...sf.props,
              fields: allFields
            }
          };
        }
        return sf;
      }
    }
  }
  return schema;
};

export const getSchema = (
  schemas: Schema[],
  type: NodeType | string | (NodeType | string)[]
) => {
  if (!schemas) return;
  if (!type) return;
  let types = type;
  if (typeof type == 'string') {
    types = [type];
  }
  let schema: Schema = null;
  for (const t of types) {
    const s = schemas?.filter(s => s && s.props.type == t)[0];
    if (s) {
      if (!schema) schema = s;
      else if (!s.isSystemSchema) {
        schema = s;
      }
    }
  }
  return schema;
};

export const SchemalessFilterSchema: Schema = {
  id: NodeType.SchemalessFilter,
  labels: [NodeType.Schema],
  props: {
    name: 'Schemaless Filter',
    displayField: null,
    refId: NodeType.SchemalessFilter,
    type: NodeType.SchemalessFilter,
    fields: [
      {
        field: 'ExcludeSecondary',
        fieldType: FieldType.custom,
        name: 'Use only primary tags for',
        key: FieldKey.ExcludeSecondary,
        displayName: 'Use only primary tags for'
      },
      {
        field: 'ItemEmployeeRelationType',
        fieldType: FieldType.custom,
        name: 'Custom Employee Group',
        key: FieldKey.ItemEmployeeRelationType,
        displayName: 'Custom Employee Group'
      }
    ]
  }
};

export const parseField = (
  fieldName: string,
  schemas: Schema[]
): FieldSchemaInfo => {
  if (!fieldName) return {} as any;
  const [schemaType, field] = fieldName.split('.') as [NodeType, string];
  if (schemaType != NodeType.SchemalessFilter) {
    const schema = getSchema(schemas, schemaType);
    const schemaField = findOne(schema?.props?.fields, { field });
    let referenceSchema = null;
    if (schemaField?.fieldType == FieldType.reference) {
      referenceSchema = getSchema(schemas, schemaField.dataType);
    }
    return {
      schemaType,
      field,
      schema,
      schemaField,
      referenceSchema
    };
  } else {
    return {
      schemaType,
      field,
      schema: SchemalessFilterSchema,
      schemaField: findOne(SchemalessFilterSchema.props.fields, { field }),
      referenceSchema: null
    };
  }
};

export const isUniMDChild = (item: Partial<Metadata>) => {
  const ctId = getReferenceValue<number>(item, 'contentType', 'id');
  if (ctId == ContentType.MARKET_DEFINITION) {
    const parentItemId = getReferenceValue(item, 'uniOf', 'itemId');
    if (parentItemId != null) {
      return true;
    }
  }
  return false;
};

export const isAuditType = (relationType: string) =>
  ['MODIFIED_BY', 'CREATED_BY'].includes(relationType);

export const getEmployeeName = (
  data: Metadata | Partial<Metadata> | Metadata[] | Partial<Metadata>[],
  opt?: {
    withEmpCd?: boolean;
    withInactive?: boolean;
    withAudit?: boolean;
    withAuditDate?: boolean;
    auditDateOnly?: boolean;
  }
) => {
  if (Array.isArray(data)) {
    return getEmployeeName(data[0], opt);
  }
  if (!data?.props) return '';
  if (!opt) return data.props.name;
  const str = [];
  if (opt.withEmpCd && data.props.empCd) {
    str.push(`(${data.props.empCd})`);
  }
  str.push(data.props.name);
  if (opt.withInactive && !data.props.isActive) {
    str.push('(Inactive)');
  }
  const processAudit = opt.withAudit || opt.withAuditDate || opt.auditDateOnly;
  if (processAudit && isAuditType(data.relationship?.type)) {
    const t = parseDate(data.relationship.props.dateTime);
    const formattedDate = formatDate(t);
    if (t && opt.withAudit) {
      str.push(`on ${formattedDate} ${t.toLocaleTimeString()}`);
    } else if (formattedDate) {
      str.push(`on ${formattedDate}`);
    }
    if (opt.auditDateOnly) {
      return formattedDate;
    }
  }
  return str.join(' ');
};

const normalizeSchemas = (
  schemas: Schema[],
  complementaryRelationType: { [x: string]: string } = {
    EQUIVALENT: 'EQUIVALENT',
    PARENT_OF: 'CHILD_OF',
    IMPLIED_BY: 'IMPLIES'
  }
): Schema[] => {
  const normalizedSchemas = clone(schemas);
  normalizedSchemas.forEach(schema => {
    const dataType = schema.props?.type;
    schema?.props?.fields.forEach(f => {
      const relationType = complementaryRelationType[f.relationType];
      if (!relationType) return;
      const targetSchema = getSchema(normalizedSchemas, f.dataType);
      if (
        targetSchema &&
        !findOne(targetSchema.props?.fields, {
          dataType,
          relationType
        })
      ) {
        targetSchema.props.fields.push({
          ...f,
          dataType,
          relationType
        });
      }
    });
  });
  return normalizedSchemas;
};

export const getParentTypes = (
  schemaType: Schema | NodeType | string,
  schemas: Schema[],
  relationTypes: string[] = ['CHILD_OF']
): NodeType[] => {
  const parentTypes = [];
  let s: Schema;
  if (typeof schemaType == 'string') {
    s = getSchema(schemas, schemaType);
  } else {
    s = schemaType;
  }

  schemas = normalizeSchemas(schemas);
  const traverseSchema = (schema: Schema) => {
    if (!schema?.props?.fields?.length) {
      return [];
    }
    schema.props.fields.forEach(f => {
      if (
        f.fieldType != FieldType.reference ||
        !relationTypes.includes(f.relationType) ||
        !f.dataType
      ) {
        return;
      }
      const parentSchema = getSchema(schemas, f.dataType);
      const parentType = parentSchema?.props?.type;
      if (parentType && !parentTypes.includes(parentType)) {
        parentTypes.push(parentSchema.props.type);
        traverseSchema(parentSchema);
      }
    });
  };
  traverseSchema(s);

  return parentTypes;
};

export const getSearchReferenceAPI = (
  field: SchemaField
): Pick<API, 'searchReferences'> => {
  if (!field.options) return null;
  return {
    searchReferences: async () =>
      field.options.reduce<Metadata[]>((arr, opt) => {
        arr.push({
          id: opt,
          props: {
            refId: opt,
            [field.field]: opt
          }
        } as Metadata);
        return arr;
      }, [])
  };
};

export const parseCondition = (
  conditions: any[],
  defaultOp?: Op
): { op: Op; val?: any } => {
  if (conditions?.length) {
    for (const [op, val] of Object.entries(conditions[0])) {
      return { op, val } as any;
    }
  }
  return { op: defaultOp };
};

export const generateHashCode = (s: any) => {
  if (!s) return null;
  if (typeof s != 'string') {
    s = JSON.stringify(s);
  }
  return s.split('').reduce((p, c) => ((p << 5) - p + c.charCodeAt(0)) | 0, 0);
};

export const useAorAn = (val: string) => {
  if (!val?.length) return 'A';
  switch (val.charAt(0).toLowerCase()) {
    case 'a':
    case 'e':
    case 'i':
    case 'o':
    case 'u':
      return 'An';
    default:
      return 'A';
  }
};

export const clone = <D>(d: D) => JSON.parse(JSON.stringify(d)) as D;

export const cn = (
  ...args: ({ [x: string]: boolean } | string | string[])[]
) => {
  const classNames = [];
  args.forEach(obj => {
    if (!obj) return;
    if (Array.isArray(obj)) {
      classNames.push(cn(...obj));
    } else if (typeof obj == 'string') {
      classNames.push(obj);
    } else {
      Object.keys(obj).forEach(k => {
        if (obj[k]) {
          classNames.push(k);
        }
      });
    }
  });
  return classNames.filter(x => x).join(' ');
};

export const isSpecialFolder = (f: View.Folder) => {
  if (!f?.props) return false;
  for (const [k, v] of Object.entries(f.props)) {
    if (k.startsWith('is') && v === true) return true;
  }
  return false;
};

export const isNullOrEmpty = (val: any) => {
  if (val === null || val === undefined) return true;
  if (Array.isArray(val) && val.length == 0) return true;
  if (typeof val == 'object' && Object.keys(val).length == 0) return true;
  return false;
};

export const isRequiredFieldValueEmpty = (value: any) => {
  return (
    value == null ||
    (typeof value == 'string' && value.trim().length == 0) ||
    (Array.isArray(value) && value.length == 0)
  );
};

export const isChildContextualization = (data: Partial<Metadata>): boolean => {
  if (data?.props?.contextualizationOf?.length) {
    return true;
  } else {
    return false;
  }
};

type itemRelationshipData = {
  parentField?: SchemaField;
  childField?: SchemaField;
  getParents: (data?: Partial<Metadata>) => Metadata[] | null;
  getChildren: (
    data?: Partial<Metadata>,
    parentRefId?: string
  ) => Metadata[] | null;
};

const defaultGetter = (schema: SchemaField) => (data?: Partial<Metadata>) => {
  if (!schema || !data?.props) return null;
  return data.props[schema.field] ?? null;
};

export const getItemRelationshipFields = (
  itemSchema: Schema,
  type: RelationshipFieldType
): itemRelationshipData => {
  let ofField: string = '';
  let byField: string = '';
  let getParents: (data?: Metadata) => Metadata[] | null = null;
  let getChildren: (
    data?: Metadata,
    parentRefId?: string
  ) => Metadata[] | null = null;

  switch (type) {
    case RelationshipFieldType.UNI:
      ofField = 'uniOf';
      byField = 'uniBy';
      break;
    case RelationshipFieldType.ProjectDerivatives:
      ofField = 'projectOf';
      byField = 'projectBy';
      break;
    case RelationshipFieldType.Companion:
      ofField = 'companionOf';
      byField = 'companionBy';
      break;
    case RelationshipFieldType.Contextualizations:
      ofField = 'contextualizationOf';
      byField = 'contextualizationBy';
      break;
    case RelationshipFieldType.DPU:
      ofField = 'dpuOf';
      byField = 'dpuBy';
      break;
    case RelationshipFieldType.Modification:
      ofField = 'modificationOf';
      byField = 'modificationBy';
      break;
    case RelationshipFieldType.Translation:
      ofField = 'translationOf';
      byField = 'translationBy';
      break;
    case RelationshipFieldType.Series:
      ofField = 'series';
      byField = 'items';
      getChildren = (data?: Partial<Metadata>, parentRefId?: string) => {
        if (!data?.props) return null;
        const parent = data.props.series?.find((s: Metadata) =>
          parentRefId != null ? s?.props?.refId == parentRefId : true
        );
        if (parent) {
          return parent.props?.items ?? null;
        }
        return null;
      };
  }

  const itemFields = itemSchema?.props?.fields;
  const parentField = findOne(itemFields, { field: ofField }) ?? null;
  const childField = findOne(itemFields, { field: byField }) ?? null;

  return {
    parentField,
    childField,
    getParents: getParents ?? defaultGetter(parentField),
    getChildren: getChildren ?? defaultGetter(childField)
  };
};

export const isEnterpriseSchema = (schema: Schema) =>
  (schema?.baseSchema?.isSystemSchema || schema?.isSystemSchema) &&
  schema?.props?.type != NodeType.Item;

export const excludeEmptyFilters = (filters: View.FiltersField[]) =>
  filters?.reduce((p, f) => {
    if (f?.field && f?.conditions?.length) {
      const filter = { ...f };
      filter.conditions = f.conditions
        .map(c => {
          const _c = { ...c };
          Object.keys(_c).forEach(op => {
            if (op == Op.isEmpty || op == Op.isNotEmpty) return;
            const val = _c[op];
            if (typeof val == 'boolean') return;
            if (
              op == Op.between &&
              Array.isArray(val) &&
              val.filter(x => x).length != 2
            ) {
              delete _c[op];
            } else if (Array.isArray(val) && !val.filter(x => x).length) {
              delete _c[op];
            } else if (typeof val != 'number' && !val) {
              delete _c[op];
            }
          });
          if (Object.keys(_c).length == 0) return null;
          return _c;
        })
        .filter(x => x);
      if (
        filter.conditions.length &&
        !p?.filter(
          existingFilter =>
            existingFilter.field == filter.field &&
            deepEquals(existingFilter.conditions, filter.conditions)
        ).length
      ) {
        p.push(filter);
      }
    }
    return p;
  }, [] as View.FiltersField[]);

export const cleanFilter = (
  filters: View.FiltersField[],
  propsToDelete: string[] = ['uuid', 'isRequired']
) =>
  filters?.map(filter => {
    const f = { ...filter };
    propsToDelete.forEach(prop => delete f[prop]);
    return f;
  });

export const throttle = (
  delay: number,
  callback: Function,
  {
    noTrailing = false,
    noLeading = false,
    debounceMode = undefined
  }: { noTrailing?: boolean; noLeading?: boolean; debounceMode?: boolean } = {}
) => {
  let timeoutID: NodeJS.Timeout,
    cancelled = false,
    lastExec = 0;
  const clear = () => {
    timeoutID = undefined;
  };
  const clearExistingTimeout = () => {
    if (timeoutID) {
      clearTimeout(timeoutID);
      clear();
    }
  };
  const throttler = (...arguments_) => {
    let self = this;
    let elapsed = Date.now() - lastExec;

    if (cancelled) {
      return;
    }

    const exec = () => {
      lastExec = Date.now();
      callback.apply(self, arguments_);
    };

    clearExistingTimeout();
    if (debounceMode === undefined && elapsed > delay) {
      if (noLeading) {
        lastExec = Date.now();
        if (!noTrailing) {
          timeoutID = setTimeout(debounceMode ? clear : exec, delay);
        }
      } else {
        exec();
      }
    } else if (noTrailing !== true) {
      timeoutID = setTimeout(
        debounceMode ? clear : exec,
        debounceMode === undefined ? delay - elapsed : delay
      );
    }
  };

  throttler.cancel = () => {
    clearExistingTimeout();
    cancelled = true;
  };
  return throttler;
};

export const debounce = <Fn extends Function>(fn: Fn, delay: number): Fn => {
  var timeoutID = null;
  return function () {
    clearTimeout(timeoutID);
    var args = arguments;
    var that = this;
    timeoutID = setTimeout(function () {
      fn.apply(that, args);
    }, delay);
  } as any;
};

export const dndMove =
  (
    fields: any,
    updateFields: Function,
    updateObject?: boolean,
    schemaFormFields: any = []
  ) =>
  (dragIndex: number, hoverIndex: number) => {
    const newFields = [...fields];
    const [draggedField] = newFields.splice(dragIndex, 1);
    newFields.splice(hoverIndex, 0, draggedField);
    const displayFieldNames = new Set(newFields.map(f => f.field));
    const mergedFields = [
      ...newFields,
      ...schemaFormFields.filter(f => !displayFieldNames.has(f.field))
    ];

    const updatedFields = updateObject
      ? { fields: mergedFields }
      : mergedFields;
    updateFields(updatedFields);
  };

export const dndCardDetails = <T>(
  onMove: Function,
  field: string,
  index: number
) => {
  if (!onMove) return { ref: null, opacity: null, handlerId: null };
  const ref = React.useRef<HTMLDivElement>(null);
  const [{ handlerId }, drop] = useDrop<
    any,
    void,
    { handlerId: Identifier | null }
  >({
    accept: 'card',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      };
    },
    hover(item: any, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      const clientOffset = monitor.getClientOffset();

      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      onMove(dragIndex, hoverIndex);
      item.index = hoverIndex;
    }
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'card',
    item: () => {
      return { field, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging()
    })
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));
  return { ref, opacity, handlerId };
};

export const getAllViews = (workspace: Workspace): View.Setting[] => {
  const allFolderViews = workspace?.folders?.reduce((prev, curr) => {
    prev.push(...curr.views);
    return prev;
  }, []);

  const allViews = [
    ...(workspace?.views || []),
    ...(allFolderViews || []),
    ...(workspace?.schemas || [])
  ];
  return allViews;
};

export const getDefaultDataKey = (nodeType: string) => 'defaultData' + nodeType;
export const getDefaultDataValue = (
  setting: WorkspaceSetting,
  nodeType: string | string[]
): string[] => {
  if (!setting?.props || !nodeType) return [];
  const nodeTypes = Array.isArray(nodeType) ? nodeType : [nodeType];
  return nodeTypes.reduce<string[]>((data, n) => {
    const key = getDefaultDataKey(n);
    const vals = setting.props[key];
    if (vals?.length) return data.concat(vals);
    return data;
  }, []);
};

export const getConfPresentationLink = (item: Partial<Metadata>): string => {
  const link = item?.props?.presentationLink;
  if (!!link) {
    if (!link.startsWith('ms-powerpoint:ofe|u|')) {
      return 'ms-powerpoint:ofe|u|' + link;
    }
    return link;
  }
  return null;
};

export const getItemType = (data: Partial<Metadata>) => {
  const itemType = data?.props?.itemType;
  if (Array.isArray(itemType)) return itemType[0];
  return itemType;
};

export const getItemStatus = (item: Partial<Metadata>): Metadata => {
  if (item?.props?.status?.length) {
    for (const m of item.props.status) {
      if (m?.props?.id) return m;
    }
  }
  return null;
};

export const getItemCreativeStatus = (item: Partial<Metadata>): Metadata => {
  if (item?.props?.creativeStatus?.length) {
    for (const m of item.props.creativeStatus) {
      if (m?.props?.id) return m;
    }
  }
  return null;
};

export const getItemStatusId = (item: Partial<Metadata>): number =>
  getItemStatus(item)?.props?.id;

export const getItemContentTypeId = (item: Partial<Metadata>): number =>
  getReferenceValue(item, 'contentType');

export const getItemContentSubTypeId = (item: Partial<Metadata>): number =>
  getReferenceValue(item, 'contentSubtype');

export const getItemResearchModelId = (item: Partial<Metadata>): number =>
  getReferenceValue(item, 'researchModel');

export const getReferenceValue = <T>(
  item: Partial<Metadata>,
  field: string,
  valKey: string = 'id'
): T => {
  if (!item?.props) return null;
  if (!item.props[field]?.length) return null;
  for (const m of item.props[field]) {
    if (m?.props && m.props[valKey] !== 'undefined') return m.props[valKey];
  }
  return null;
};

export const getNextItemStatusId = (itemStatusId: number): number => {
  if (itemStatusId == ItemStatus.NotYetCreated) {
    return ItemStatus.Idea;
  } else if (itemStatusId == ItemStatus.Idea) {
    return ItemStatus.Concept_Review;
  } else if (itemStatusId == ItemStatus.Concept_Review) {
    return ItemStatus.Review_Done_Start_Production;
  } else if (itemStatusId == ItemStatus.Not_Pursued) {
    return ItemStatus.Concept_Review;
  }
  return null;
};

const getRequiredFieldsByItemData = (
  data: Partial<Metadata>,
  itemStatusId: number
) => {
  if (!data?.props || !data?.labels?.includes(NodeType.Item)) return [];
  const contentTypeId = getItemContentTypeId(data);
  const hasUniRelationship =
    data?.props?.uniBy?.length > 0 || data?.props?.uniOf?.length > 0;
  if (
    contentTypeId == ContentType.MAGIC_QUADRANT &&
    !hasUniRelationship &&
    [
      ItemStatus.Concept_Review,
      ItemStatus.Review_Done_Start_Production
    ].includes(itemStatusId)
  ) {
    return magicQuadrantFields;
  }
  if (
    contentTypeId == ContentType.Pilot &&
    [
      ItemStatus.Concept_Review,
      ItemStatus.Review_Done_Start_Production
    ].includes(itemStatusId)
  ) {
    return pilotFields;
  }
  return [];
};

export const getRequiredFieldsToPushToCPP = (
  data: Partial<Metadata>,
  overrideItemStatusId?: number
): string[] => {
  if (!data?.props || !data?.labels?.includes(NodeType.Item)) return [];
  const itemType = getItemType(data);
  const itemStatusId = overrideItemStatusId || getItemStatusId(data);
  const contentTypeId = getItemContentTypeId(data);
  const requiredFields = getRequiredFields(
    itemType,
    itemStatusId,
    contentTypeId
  );
  const evolvingInsights = data?.props?.evolvingInsights;
  if (evolvingInsights) {
    return [...requiredFields, 'contentType'];
  }
  return requiredFields;
};

export const fieldIsRequired = (
  field: SchemaField,
  data: Partial<Metadata>,
  overrideItemStatusId?: number
): boolean => {
  if (!field) return;
  if (isFieldDisabled(field, data)) {
    return false;
  }
  //to do: remove in 6.0 and beyond
  if (field?.dataType == ('enum' as any) && field?.field == 'itemType') {
    return false;
  }
  const itemStatusId = overrideItemStatusId || getItemStatusId(data);
  const requiredFieldsToPush = getRequiredFieldsToPushToCPP(
    data,
    overrideItemStatusId
  );
  const requiredFieldsByItemData = getRequiredFieldsByItemData(
    data,
    itemStatusId
  );

  const willFieldBeRequiredForPush =
    [ItemStatus.Review_Done_Start_Production].includes(itemStatusId) &&
    !!requiredFieldsToPush?.filter(f => field.field == f)?.length;

  const willFieldBeRequiredByItemData = !!requiredFieldsByItemData?.filter(
    f => field.field == f
  ).length;

  if (field.requiredAt) {
    switch (field.requiredAt) {
      case FieldRequiredValue.ConceptReview:
        return [ItemStatus.Concept_Review].includes(itemStatusId);
      case FieldRequiredValue.ReviewDone:
        return (
          [ItemStatus.Review_Done_Start_Production].includes(itemStatusId) ||
          willFieldBeRequiredForPush
        );
    }
  }
  return (
    field.requiredAt == FieldRequiredValue.Required ||
    willFieldBeRequiredForPush ||
    willFieldBeRequiredByItemData
  );
};

const isFieldDisabled = (field: SchemaField, data: Partial<Metadata>) => {
  if (
    field.fieldType == FieldType.computed ||
    (field.isSystemField &&
      data?.labels?.includes(NodeType.Item) &&
      (data?.props?.itemId ||
        data?.props?.status?.filter((s: Metadata) => s?.props?.id == 12)
          .length > 0))
  ) {
    return true;
  }
  return false;
};

export const fieldIsRequiredNext = (
  field: SchemaField,
  d: Partial<Metadata>,
  impliedDataProps: MetadataProps
): boolean => {
  if (!field) return;
  let data = d;
  if (impliedDataProps?.contentType?.length) {
    data = {
      ...(data || {}),
      props: {
        ...(data?.props || {}),
        contentType: impliedDataProps.contentType
      }
    };
  }
  const itemStatusId = getItemStatusId(data);
  const nextItemStatusId = getNextItemStatusId(itemStatusId);
  const reqNext = fieldIsRequired(field, data, nextItemStatusId);
  return reqNext;
};

export const getInvalidItemDataReference = (
  workspace: Workspace,
  schema: Schema,
  data: Partial<Metadata>
) => {
  if (
    workspace?.disableItemDataRefIds?.length &&
    schema?.props?.type == NodeType.Item
  ) {
    const cppFields = schema.props.fields.filter(
      f => f.isSystemField && f.fieldType == FieldType.reference
    );
    for (const f of cppFields) {
      const refs = data.props[f.field] || [];
      for (const ref of refs) {
        if (workspace?.disableItemDataRefIds.includes(ref?.props?.refId)) {
          return ref;
        }
      }
    }
  }
};

export const getLockedNodeTypes = (workspace: Workspace) => {
  const wsSetting = workspace.setting?.props;
  if (!wsSetting || !workspace.schemas?.length) return [];
  return workspace.schemas
    .filter(s => wsSetting[`lockedNode${s?.props?.type}`])
    .map(s => s.props.type);
};

export const getIntersection = <T>(arr1: T[], arr2: T[]): T[] => {
  if (!arr1?.length || !arr2?.length) return [];
  return arr1.filter(a => arr2.includes(a));
};

export const stringToColor = function (str: string) {
  var hash = 0;
  for (var i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  var colour = '#';
  for (var i = 0; i < 3; i++) {
    var value = (hash >> (i * 8)) & 0xff;
    colour += ('00' + value.toString(16)).substr(-2);
  }
  return colour;
};

export const addIsImpliedValue = (m: Metadata): Metadata => {
  return { ...m, isImplied: true };
};

export const isBlanks = (m: Metadata | any) => {
  if (!m) return false;
  if (typeof m == 'string') return m == 'blanks';
  return m.id == 'blanks' || m.props?.refId == 'blanks';
};

export const getFieldValue = (field: SchemaField, data: Partial<Metadata>) => {
  const isDataSource = field?.key == 'Item.dataSourceProjects';
  if (field?.fieldType == FieldType.reference || isDataSource) {
    let value: Metadata[] = null;
    let f = isDataSource ? 'dataSource' : field.field;

    if (data?.props) {
      value = data.props[f];
    }
    if (!Array.isArray(value)) value = [];

    return !value?.length ? null : value;
  } else if (field?.dataType == DataType.boolean) {
    let value = null;
    if (data?.props) {
      value = data.props[field.field];
      if (value && typeof value == 'string') {
        const v = value.toLowerCase();
        value = ['y', 'yes', 'true'].indexOf(v) >= 0;
      }
      if (!value || typeof value != 'boolean') value = false;
    }
    return value;
  } else if (field?.dataType == DataType.number) {
    let value = '';
    if (data?.props) {
      value =
        data.props[field.field] !== undefined ? data.props[field.field] : null;
    }
    return value;
  } else if (
    field?.dataType == DataType.longString ||
    field?.dataType == DataType.string
  ) {
    let value = '';
    if (data?.props) {
      value = data.props[field.field] || '';
    }
    return value;
  } else if (field?.dataType == DataType.date) {
    let value = null;
    if (data?.props) {
      value = parseDate(data.props[field.field]);
    }
    return value;
  } else if (field?.dataType == DataType.enum) {
    let value = null;
    if (data?.props) {
      value = data.props[field.field];
    }
    return value;
  }
};

export const getRelationshipOf = (
  data: Partial<Metadata>,
  relationshipTypeIds: number[],
  by?: boolean
) => {
  if (!data?.props) return [];
  return Object.entries(data.props).reduce((arr, [key, val]) => {
    if (key.endsWith(by ? 'By' : 'Of') && val && Array.isArray(val)) {
      val.forEach(m => {
        if (typeof m == 'object' && m?.relationship?.type == 'ITEM_ITEM') {
          if (
            relationshipTypeIds.includes(
              m.relationship.props?.relationshipTypeId
            )
          ) {
            arr.push(m);
          }
        }
      });
    }
    return arr;
  }, []);
};

export const isKeepActiveEnabled = (item: Partial<Metadata>) => {
  const currentDay = new Date();
  currentDay.setHours(0, 0, 0, 0);
  const itemStatusId = getItemStatusId(item);
  const itemType = getItemType(item);
  return (
    [
      ItemStatus.Published,
      ItemStatus.Complete,
      ItemStatus.PublishedHistoricalNonCPP
    ].includes(itemStatusId) &&
    !!item.props.expiryDate &&
    parseDate(item.props.expiryDate) > currentDay &&
    itemType == ItemType.content
  );
};

export const isArchiveEnabled = (item: Partial<Metadata>) => {
  const itemStatusId = getItemStatusId(item);
  return (
    [
      ItemStatus.Published,
      ItemStatus.Complete,
      ItemStatus.PublishedHistoricalNonCPP
    ].includes(itemStatusId) &&
    item.props.expiryRequestType != 'Pending Archive'
  );
};

export const isUpdateDisabled = (item: Partial<Metadata>) => {
  const itemContentTypeId = getItemContentTypeId(item);
  const itemStatusId = getItemStatusId(item);
  const itemType = getItemType(item);
  return (
    !item.props.itemId ||
    isRemovedOrInactive(itemStatusId) ||
    itemStatusId == ItemStatus.RetractedTemporary ||
    itemType != ItemType.content
  );
};

export const showBackupMode = (view: View.Setting): boolean => {
  return (
    !!view?.props?.isMyTasks ||
    !!view?.props?.isMyTeam ||
    !!view?.props?.isMyExpiryDashboard
  );
};

export const findOneByProps = <P, T, A>(
  list: Metadata<P, T, A>[],
  props: Partial<P>
): Metadata<P, T, A> => {
  return findByProps(list, props)[0];
};
export const findByProps = <P, T, A>(
  list: Metadata<P, T, A>[],
  props: Partial<P>
): Metadata<P, T, A>[] => {
  return (list || []).filter(l => {
    const p = l.props || {};
    const m = { ...p, ...props };
    for (let key of Object.keys(m)) {
      if (m[key] != p[key]) {
        return false;
      }
    }
    return true;
  });
};

export const isRemovedOrRetired = (item: Partial<Metadata> | number) => {
  if (typeof item == 'number') {
    return item == ItemStatus.Removed || item == ItemStatus.Retired;
  } else {
    const itemStatusId = getItemStatusId(item);
    return (
      itemStatusId == ItemStatus.Removed || itemStatusId == ItemStatus.Retired
    );
  }
};

export const isRemovedOrInactive = (item: Partial<Metadata> | number) => {
  if (typeof item == 'number') {
    return (
      item == ItemStatus.Removed ||
      item == ItemStatus.Retired ||
      item == ItemStatus.PendingRetirement
    );
  } else {
    const itemStatusId = getItemStatusId(item);
    return (
      itemStatusId == ItemStatus.Removed ||
      itemStatusId == ItemStatus.Retired ||
      itemStatusId == ItemStatus.PendingRetirement
    );
  }
};

export const getAndFlattenCustomFieldData = (
  data: Partial<Metadata>,
  schemaForm: Schema
) => {
  return schemaForm?.props?.fields
    ?.filter(f => !f?.isSystemField)
    ?.map(f => {
      if (!!data?.props?.[f.field]) {
        if (f?.dataType == DataType.date || f?.dataType == DataType.dateTime) {
          return { [f.field]: formatDate(data?.props?.[f.field], true) };
        } else {
          return { [f.field]: data?.props?.[f.field] };
        }
      } else {
        return null;
      }
    })
    ?.filter(f => f)
    ?.reduce((prev, curr) => {
      return { ...prev, ...curr };
    }, {});
};

export const getCardFields = (
  card: View.Card,
  userSetting: UserSetting
): View.CardField[] => {
  const cardFields = card?.props?.fields || [];
  if (!userSetting?.props) return cardFields;
  const fields = userSetting.props.fields || [];
  const deletedFields = userSetting.props.deletedFields || [];
  if (!fields.length && !deletedFields.length) return cardFields;

  return fields
    .map(field => ({ field }))
    .concat(
      cardFields.filter(
        f => !deletedFields.includes(f.field) && !fields.includes(f.field)
      )
    );
};

export const getFilteredDataSourcesAndProjects = (data: Partial<Metadata>) => {
  const filteredDataSources = data?.props?.dataSource?.filter(ds => {
    if (ds?.props?.id == 15498) return false;
    if (ds?.props?.isActive == false) return false;
    const dsProjects = data?.props?.project?.filter(
      p => p.props.dataSourceId === ds?.props?.id
    );
    if (!dsProjects?.length) return true;
    return dsProjects.some(p => p.props.isActive === true);
  });
  const filteredProjects = data?.props?.project
    ?.filter(p => p.props.isActive === true)
    ?.filter(p =>
      filteredDataSources?.some(ds => ds?.props?.id === p.props.dataSourceId)
    );
  const filteredDataSourceItems = data?.props?.dataSourceItem;
  return { filteredDataSources, filteredProjects, filteredDataSourceItems };
};

const useDisplayOrder = (a, b) => {
  const aHasOrder = a.props?.displayOrder != undefined;
  const bHasOrder = b.props?.displayOrder != undefined;
  if (aHasOrder && bHasOrder) {
    return a.props.displayOrder - b.props.displayOrder;
  }
  if (aHasOrder) return -1;
  if (bHasOrder) return 1;
  return 0;
};

const useAlphaOrder = (a, b) => {
  return a.props.name.localeCompare(b.props.name, undefined, {
    sensitivity: 'base'
  });
};

const insertCopies = list => {
  const result = [];
  const used = new Set();
  for (let i = 0; i < list.length; i++) {
    if (used.has(i)) continue;
    const item = list[i];
    result.push(item);
    const copyIndex = list.findIndex(
      (v, idx) =>
        idx != i &&
        !used.has(idx) &&
        v.props.name.trim().toLowerCase() ==
          (item.props.name.trim() + ' Copy').toLowerCase()
    );
    if (copyIndex != -1) {
      result.push(list[copyIndex]);
      used.add(copyIndex);
    }
    used.add(i);
  }
  for (let i = 0; i < list.length; i++) {
    if (!used.has(i)) result.push(list[i]);
  }
  return result;
};

export const sortViewsByDisplayOrder = (views: View.Setting[]) => {
  if (!views?.length) return [];
  const withOrder = views
    .filter(v => v.props.displayOrder != undefined)
    .sort(useDisplayOrder);
  let withoutOrder = views.filter(v => v.props.displayOrder == undefined);
  withoutOrder = withoutOrder.sort(useAlphaOrder);
  withoutOrder = insertCopies(withoutOrder);
  return [...withOrder, ...withoutOrder];
};
export const sortFoldersByDisplayOrder = (folders: View.Folder[]) => {
  if (!folders?.length) return [];
  const specialFolders: View.Folder[] = [];
  const rest: View.Folder[] = [];
  specialFolders.push(
    folders[folders.findIndex(f => f.props.isBacktagging)],
    folders[folders.findIndex(f => f.props.isFavorite)],
    folders[folders.findIndex(f => f.props.isMyItems)]
  );
  for (const f of folders) {
    if (!specialFolders.includes(f)) rest.push(f);
  }
  specialFolders.forEach(f => {
    if (f && Array.isArray(f.views)) {
      f.views = [...f.views].sort(useAlphaOrder);
    }
  });
  const restWithOrder = rest
    .filter(f => f.props.displayOrder != undefined)
    .sort(useDisplayOrder);
  let restWithoutOrder = rest.filter(f => f.props.displayOrder == undefined);
  restWithoutOrder = restWithoutOrder.sort(useAlphaOrder);
  restWithoutOrder = insertCopies(restWithoutOrder);
  [...restWithOrder, ...restWithoutOrder].forEach(f => {
    if (Array.isArray(f.views)) {
      f.views = sortViewsByDisplayOrder(f.views);
    }
  });
  return [...specialFolders, ...restWithOrder, ...restWithoutOrder].filter(
    x => x
  );
};

export const canActionTask = (
  approval: ApprovalTask,
  backingUpFor: View.BackingUpFor,
  actionEmployeeId: number
) => {
  if (!approval?.taskAssignee?.length) return false;
  return approval.taskAssignee.some(ta => {
    const isDirect =
      backingUpFor == null && ta.props?.empCd == actionEmployeeId;
    const isBackup =
      ta.props?.empCd == backingUpFor?.empCd &&
      backingUpFor?.roleIds?.includes(approval?.roleId);
    return isDirect || isBackup;
  });
};

export const openBusinessCaseModal = (
  data: Partial<Metadata>,
  backingUpFor: View.BackingUpFor,
  actionEmployeeId: number,
  adminApproval: boolean
) => {
  const tmApproval =
    data?.approvals?.filter(task => task?.role == 'teamManager') || [];

  return adminApproval
    ? !tmApproval.some(task => task?.approvalStatus == ApprovalType.Approved)
    : tmApproval.some(tmTask =>
        canActionTask(tmTask, backingUpFor, actionEmployeeId)
      );
};

export const AUTHOR_PROMOTE_CONTENT_TYPES = [
  ContentType.Analyst_Take,
  ContentType.First_Take
];

export const isAuthorPromotableAtConceptReview = (
  empCd: number,
  leadAuthorEmpCd: number,
  contentTypeId: number,
  itemStatusId: number
): boolean =>
  empCd === leadAuthorEmpCd &&
  AUTHOR_PROMOTE_CONTENT_TYPES.includes(contentTypeId) &&
  itemStatusId === ItemStatus.Concept_Review;

export const openConfigureWorkflowModal = (
  data: Partial<Metadata>,
  backingUpFor: View.BackingUpFor,
  actionEmployeeId: number,
  action: string,
  adminApproval: boolean,
  creativeHelp: boolean
) => {
  const contentTypeId = getItemContentTypeId(data);
  const teamManagerApproval = data.approvals
    ?.filter(task => task?.role == 'teamManager')
    ?.some(tmTask => canActionTask(tmTask, backingUpFor, actionEmployeeId));
  if (!teamManagerApproval && !adminApproval) return false;
  if (action != ApprovalType.Approved) return false;
  if (
    !!data?.props?.configureWorkflowType &&
    (data?.props?.configureWorkflowType != ConfigureWorkflowType.Branded ||
      !creativeHelp ||
      typeof data?.props?.requestCreativeHelp == 'boolean')
  )
    return false;
  if (getItemStatusId(data) != ItemStatus.Concept_Review) return false;
  if (AUTHOR_PROMOTE_CONTENT_TYPES.includes(contentTypeId)) return false;
  if (contentTypeId == ContentType.MARKET_DEFINITION) return false;
  if (
    contentTypeId == ContentType.MAGIC_QUADRANT &&
    (data?.props?.uniBy?.length > 0 || data?.props?.uniOf?.length > 0)
  )
    return false;
  return true;
};

export const skipConfigureWorkflowValidation = (data: Partial<Metadata>) => {
  const backingUpFor = useSelector(s => s.view?.backingUpFor);
  const actionEmployeeId = useSelector(
    s => s.view?.actingAs?.props?.empCd || s.app?.user?.employeeId
  );
  const adminApproval =
    useSelector(s => s.app?.user?.roles?.includes(Role.admin)) &&
    process.env.ENVIRONMENT != 'PROD' &&
    process.env.ENVIRONMENT != 'DEVB';
  const contentTypeId = getItemContentTypeId(data);
  const teamManagerApproval = data?.approvals
    ?.filter(task => {
      return task?.role == 'teamManager';
    })
    ?.some(tmTask => {
      return canActionTask(tmTask, backingUpFor, actionEmployeeId);
    });
  if (teamManagerApproval || adminApproval) return false;
  if (getItemStatusId(data) != ItemStatus.Concept_Review) return false;
  if (contentTypeId == ContentType.MARKET_DEFINITION) return false;
  if (
    contentTypeId == ContentType.MAGIC_QUADRANT &&
    (data?.props?.uniBy?.length > 0 || data?.props?.uniOf?.length > 0)
  )
    return false;
  return true;
};

export const getInheritedContentTypeConfiguration = (
  configurations: ContentTypeConfig[],
  researchModelId: number,
  contentTypeId: number,
  subtypeId: number
): ContentTypeConfig =>
  configurations?.find(
    cfg =>
      cfg?.props?.modelId == researchModelId &&
      cfg?.props?.dtypCd == contentTypeId &&
      cfg?.props?.dstpCd == subtypeId
  );

export const hideHiddenFields = (
  data: Partial<Metadata>,
  schema: Schema,
  userSettings: UserSettings
) => {
  if (!data?.props || !schema?.props) return true;
  if (!userSettings) return false;
  if (schema.props.type == NodeType.Item) {
    const appSetting = userSettings[AppRefId];
    const showFieldsOption = appSetting?.props?.showFieldsOption;
    if (showFieldsOption === ShowFieldsOption.All) return false;
    return true;
  }
  return false;
};

export const getShowFieldsOption = (
  data: Partial<Metadata>,
  userSettings: UserSettings
): ShowFieldsOption => {
  const appSetting = userSettings?.[AppRefId];
  const showFieldsOption = appSetting?.props?.showFieldsOption;
  if (!showFieldsOption) {
    return ShowFieldsOption.Default;
  }
  const isPlanningItem = !data?.props?.itemId;
  const itemStatusId = getItemStatusId(data);
  if (!isPlanningItem || itemStatusId === ItemStatus.PendingCPPItemCreation) {
    switch (showFieldsOption) {
      case ShowFieldsOption.RequiredForPhase:
      case ShowFieldsOption.RequiredToPromote:
        return ShowFieldsOption.Default;
      default:
        return showFieldsOption;
    }
  }
  return showFieldsOption;
};

export const getFieldsToDisplay = (
  schema: Schema,
  data: Partial<Metadata>,
  showFieldsOption: ShowFieldsOption,
  impliedDataProps: MetadataProps,
  errorWarningFields?: string[]
): SchemaField[] => {
  if (!schema?.props?.fields) return [];
  const schemaForm = getSchemaForm(schema, data, true);
  const { type, fields } = schemaForm.props;
  if (type != NodeType.Item) return fields;
  const contentTypeId = getItemContentTypeId(data);

  if (errorWarningFields?.length) {
    fields.forEach(f => {
      if ([FieldShowValue.Hide, FieldShowValue.AlwaysHide].includes(f.showAt)) {
        const ts = TreeFieldUtils.getTreeSetting(schema, f);
        if (ts?.settings?.length) {
          const p = ts.settings.shift();
          for (const s of ts.settings) {
            if (errorWarningFields.includes(s?.field?.key)) {
              errorWarningFields.push(p?.field?.key);
              break;
            }
          }
        }
      }
    });
  }

  const requiredParentFields = new Set<string>();

  const mappedFields = fields.map(f => {
    const isRequired = fieldIsRequired(f, data);
    const isRequiredNext = fieldIsRequiredNext(f, data, impliedDataProps);
    let isHidden = [FieldShowValue.Hide, FieldShowValue.AlwaysHide].includes(
      f.showAt
    );
    const isRequireNextdByContentType =
      [ContentType.MAGIC_QUADRANT, ContentType.Pilot].includes(contentTypeId) &&
      isRequiredNext;
    const itemStatusId = getItemStatusId(data);
    if (f.showAt && !isHidden && itemStatusId) {
      switch (f.showAt) {
        //case FieldShowValue.IdeaCreation:
        case FieldShowValue.Idea:
          if (!data.id || ItemStatus.Not_Pursued == itemStatusId) {
            isHidden = true;
          }
          break;
        case FieldShowValue.ConceptReview:
          if (
            !data.id ||
            [ItemStatus.Idea, ItemStatus.Not_Pursued].includes(itemStatusId)
          ) {
            isHidden = true;
          }
          break;
      }
    }

    if (f.parentField) {
      const isErrorField = errorWarningFields?.includes(f.key);
      const shouldAddParentField =
        isErrorField ||
        (showFieldsOption === ShowFieldsOption.RequiredForPhase
          ? isRequired
          : isRequired || isRequiredNext);
      if (shouldAddParentField) requiredParentFields.add(f.parentField);
    }

    return {
      ...f,
      schemaField: f,
      isRequired,
      isRequiredNext,
      isHidden: isHidden && !isRequired && !isRequireNextdByContentType,
      isAlwaysHidden:
        f.showAt == FieldShowValue.AlwaysHide &&
        !isRequired &&
        !isRequireNextdByContentType
    };
  });

  return mappedFields
    .filter(f => {
      if (errorWarningFields?.includes(f.key)) return true;
      if (f.isAlwaysHidden) return false;
      const isAlwaysRequired = f.requiredAt === FieldRequiredValue.Required;
      switch (showFieldsOption) {
        case ShowFieldsOption.All:
          return true;
        case ShowFieldsOption.RequiredForPhase:
          return (
            isAlwaysRequired ||
            f.isRequired ||
            requiredParentFields.has(f.field)
          );
        case ShowFieldsOption.RequiredToPromote:
          return (
            isAlwaysRequired ||
            f.isRequired ||
            f.isRequiredNext ||
            requiredParentFields.has(f.field)
          );
        default:
          return !f.isHidden;
      }
    })
    .map(f => f.schemaField);
};

export const getSkipWorkflowStatuses = (
  config: ContentTypeConfig,
  isUniOf: boolean,
  evolvingInsights: boolean,
  isProofreadingOnly: boolean
) => {
  const skipWorkflows = config?.props?.skipWorkflow || [];
  if (isUniOf) {
    skipWorkflows.push(ItemStatus.Outline);
    skipWorkflows.push(ItemStatus.OutlineReview);
  }
  if (evolvingInsights) {
    skipWorkflows.push(ItemStatus.DraftPeerReview);
  }
  if (isProofreadingOnly) {
    skipWorkflows.push(ItemStatus.InitialEditingIntake);
  }
  return skipWorkflows;
};

export const getSchemaFieldNameByField = (
  schemas: Schema[],
  fieldName: string
) => {
  const itemSchema = getSchema(schemas, NodeType.Item);
  const field = findOne(itemSchema.props.fields, {
    field: fieldName
  });
  return field?.name || fieldName;
};

export const getWarningMessages = (
  isFirstTake: boolean,
  isBranded: boolean,
  canConfigureWorkflow: boolean,
  leadAuthorEmpCd: number,
  authorInfo: WorkflowInfo,
  researchModelId: number,
  contentTypeId: number,
  config: ContentTypeConfig,
  requestCreativeHelpFieldName: string,
  canConfigureCreativeHelp: boolean,
  canSaveWorkflow?: boolean,
  configurableExternalReviewNames?: string[],
  canConfigureExternalReview?: boolean
): { message: string; warningType: ConfigureWorkflowWarningType }[] =>
  React.useMemo(() => {
    const list: {
      message: string;
      warningType: ConfigureWorkflowWarningType;
    }[] = [];
    if (isBranded || isFirstTake) {
      const ctType = isFirstTake ? 'First Take' : 'a branded content type';
      const configurableOptions: string[] = [];
      if (config?.props?.creativeHelp) {
        configurableOptions.push(requestCreativeHelpFieldName);
      }
      if (configurableExternalReviewNames?.length > 0) {
        configurableOptions.push(configurableExternalReviewNames.join('/'));
      }
      if (configurableOptions.length > 0) {
        list.push({
          message: `This is ${ctType}; only ${configurableOptions.join(' and ')} can be configured.`,
          warningType: ConfigureWorkflowWarningType.WORKFLOW
        });
      } else {
        list.push({
          message: `This is ${ctType} and is not configurable.`,
          warningType: ConfigureWorkflowWarningType.BOTH
        });
      }
    }
    if (!leadAuthorEmpCd)
      list.push({
        message:
          'Lead Author is required before you can customize the workflow.',
        warningType: ConfigureWorkflowWarningType.BOTH
      });

    if (
      !canConfigureWorkflow &&
      !canConfigureCreativeHelp &&
      !canConfigureExternalReview
    ) {
      if (canSaveWorkflow) {
        list.push({
          message:
            'Only the Team Manager or Workspace Owner can customize the workflow of this document. Your role only permits saving the default workflow.',
          warningType: ConfigureWorkflowWarningType.WORKFLOW
        });
      } else if (!leadAuthorEmpCd || (!!leadAuthorEmpCd && !!authorInfo))
        list.push({
          message:
            'Only the Team Manager or Workspace Owner can customize the workflow of this document.',
          warningType: ConfigureWorkflowWarningType.BOTH
        });
    }
    const missing = [];
    if (!researchModelId) missing.push('Research Model');
    if (!contentTypeId) missing.push('Content Type');
    if (missing.length > 0) {
      list.push({
        message: `${missing.join(' and ')} ${
          missing.length == 1 ? 'is' : 'are'
        } required before you can customize the workflow.`,
        warningType: ConfigureWorkflowWarningType.BOTH
      });
    }
    return list;
  }, [
    isFirstTake,
    isBranded,
    canConfigureWorkflow,
    canConfigureCreativeHelp,
    leadAuthorEmpCd,
    authorInfo,
    contentTypeId,
    researchModelId,
    canSaveWorkflow,
    canConfigureExternalReview,
    config,
    requestCreativeHelpFieldName,
    configurableExternalReviewNames
  ]);

export const getConfigureWorkflowTypeEnums = (workflowTypeField: string) =>
  useSelector(s => {
    const itemSchema = getSchema(
      s.planning.selectedWorkspace.schemas,
      NodeType.Item
    );
    const field = findOne(itemSchema.props.fields, {
      field: workflowTypeField
    });
    return field.enumValues;
  });

export const getDataValues = (data: Partial<Metadata>) =>
  React.useMemo(() => {
    if (!data?.props) return {};
    const itemRefId = data.props.refId;
    const homeWorkspaceRefId = data.props.homeWorkspaceRefId;
    const itemStatusId = getItemStatusId(data);
    const itemCreativeStatusId = getItemCreativeStatus(data);
    const researchModelId = getItemResearchModelId(data);
    const contentTypeId = getItemContentTypeId(data);
    const subtypeId = getItemContentSubTypeId(data);
    const uniOf = getReferenceValue(data, 'uniOf', 'refId');
    const evolvingInsights = data.props.evolvingInsights;
    const leadAuthorEmpCd = getReferenceValue<number>(data, 'author', 'empCd');
    const isBranded =
      getReferenceValue(data, 'contentType', 'isBranded') == true ||
      getReferenceValue(data, 'contentSubtype', 'isBranded') == true;
    const isProofreadingOnly =
      getReferenceValue(data, 'contentType', 'isProofreadingOnly') == true ||
      getReferenceValue(data, 'contentSubtype', 'isProofreadingOnly') == true;
    const isFirstTake =
      getReferenceValue(data, 'workflowPrioritization', 'id') ==
      WorkflowPrioritization.FirstTake;
    const isExcludeFromEditorialTokenCount =
      data.props.excludeFromEditorial === true;
    const isExcludeFromCreativeTokenCount =
      data.props.excludeFromCreative === true ||
      getReferenceValue(data, 'contentType', 'excludeFromCreativeTokenCount') ==
        true ||
      getReferenceValue(
        data,
        'contentSubtype',
        'excludeFromCreativeTokenCount'
      ) == true;

    return {
      itemRefId,
      homeWorkspaceRefId,
      itemStatusId,
      itemCreativeStatusId,
      researchModelId,
      contentTypeId,
      subtypeId,
      uniOf,
      evolvingInsights,
      leadAuthorEmpCd,
      isFirstTake,
      isExcludeFromEditorialTokenCount,
      isExcludeFromCreativeTokenCount,
      isProofreadingOnly,
      isBranded
    };
  }, [data]);

export const isModified = (
  data: Partial<Metadata>,
  newIds: number[],
  requestCreativeHelp: boolean,
  initialSkipIds?: number[],
  preProductionExternalReview?: boolean,
  postProductionExternalReview?: boolean
) =>
  React.useMemo(() => {
    const oldIds = initialSkipIds ?? data.props?.configureWorkflowSkipIds ?? [];
    const newSkipIds = newIds ?? [];
    const oldRequestCreativeHelp = data.props?.requestCreativeHelp || false;
    const oldPreProductionExternalReview =
      data.props?.preProductionExternalReview || false;
    const oldPostProductionExternalReview =
      data.props?.postProductionExternalReview || false;

    if (
      requestCreativeHelp != oldRequestCreativeHelp ||
      preProductionExternalReview != oldPreProductionExternalReview ||
      postProductionExternalReview != oldPostProductionExternalReview
    ) {
      return true;
    }
    const nIds = removeDuplicates(newSkipIds);
    const oIds = removeDuplicates(oldIds);
    nIds.sort();
    oIds.sort();
    return nIds.join(',') != oIds.join(',');
  }, [
    newIds,
    data,
    requestCreativeHelp,
    preProductionExternalReview,
    postProductionExternalReview
  ]);

export const getCreativeHelpDisabled = (
  workflowInfo: WorkflowInfo,
  itemCreativeStatusId: any,
  data: Partial<Metadata>,
  config: ContentTypeConfig,
  itemStatusId: number,
  isNewLightUpdate: boolean
) =>
  !isNewLightUpdate &&
  (!workflowInfo?.isWipItem ||
    (!!itemCreativeStatusId &&
      (!data?.props?.requestCreativeHelp ||
        itemCreativeStatusId?.props?.id > CreativeStatus.CreativeIntake ||
        itemCreativeStatusId?.props?.id == CreativeStatus.CreativeBypassed)) ||
    !config?.props?.creativeHelp ||
    (itemStatusId > ItemStatus.TeamManagerSignoff &&
      ![
        ItemStatus.LightUpdateDraft,
        ItemStatus.LightUpdateFinalSignoff
      ].includes(itemStatusId)));
export { ConfigureWorkflowWarningType };

export const getFieldLabel = (data, newItemName, name) => {
  return !data?.props?.refId && !!newItemName ? newItemName : name;
};
